<template>
	<!-- Custom modal wrapper to have full control over styling -->
	<transition name="modal">
		<div v-if="show">
			<div
				ref="modalContainer"
				class="fixed inset-0 z-50 flex items-center justify-center p-4"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				@click="$emit('close')"
			>
				<!-- Figma spec: width: 600px, padding: 24px, gap: 16px, border-radius: 10px, border: 1px solid rgba(0,0,0,0.1), shadow -->
				<div 
					class="w-[600px] p-6 flex flex-col items-center gap-4 bg-white rounded-[10px] border border-[rgba(0,0,0,0.1)]" 
					style="box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10);"
					@click.stop
				>
			<!-- Header with close button -->
			<div class="w-full flex items-center justify-between">
				<h5 class="text-base font-medium text-[#0a0a0a] leading-[20.8px] tracking-[0.3px]">
					{{ $t('crud.activate-job') }}: {{ jobOffer.name }}
				</h5>
				<Button
					icon="times"
					type="tertiary"
					icon-size="lg"
					@click="$emit('close')"
				></Button>
			</div>

			<!-- Multiposting Card -->
			<div class="w-full bg-white border border-[#e3e3e3] rounded-lg p-6 flex flex-col gap-4">
				<!-- Card Content Row: Text + Toggle -->
				<div class="w-full flex items-start gap-3">
					<!-- Text Content -->
					<div class="flex-1 flex flex-col gap-1.5">
						<p class="text-base font-normal text-[#171717] leading-[20.8px] tracking-[0.3px] mb-0">
							{{ $t('job-offer.activate-modal.share-job-external-boards') }}
						</p>
						<p class="text-sm font-normal text-[#737373] leading-[20px] tracking-[0.3px] mb-0">
							{{
								$t(
									'job-offer.activate-modal.multiposting-reach-text'
								)
							}}
						</p>
					</div>
					<!-- Toggle -->
					<Toggle v-model="enableMultiposting" />
				</div>

				<!-- Status Message (Always Visible) -->
				<div class="flex items-center gap-2">
					<!-- Icon -->
					<Icon
						:icon="
							!enableMultiposting
								? ['far', 'info-circle']
								: hasConfiguredLinkedIn
								? ['far', 'check-circle']
								: ['far', 'info-circle']
						"
						class="w-4 h-4 text-[#303030] flex-shrink-0"
					/>
					<!-- Text -->
					<div class="text-sm font-medium text-[#303030] leading-[20px] tracking-[0.3px]">
							<template v-if="!enableMultiposting">
								{{ $t('job-offer.activate-modal.visible-only-on-platform') }}
							</template>
							<template v-else-if="hasConfiguredLinkedIn">
								{{ $t('job-offer.activate-modal.job-visible-externally-linkedin') }}
							</template>
							<template v-else>
								{{ $t('job-offer.activate-modal.linkedin-not-set-warning') }}
								<!-- keep a literal space between sentence and link -->
								<span> </span>
								<a
									v-if="$can('edit organization')"
									role="button"
									tabindex="0"
									class="underline hover:no-underline cursor-pointer"
									@click="openLinkedInSettings"
									@keydown.enter="openLinkedInSettings"
								>
									{{ $t('job-offer.activate-modal.company-settings') }}
								</a>
							</template>
					</div>
				</div>
			</div>

			<!-- Footer Buttons -->
			<div class="w-full flex items-center justify-end gap-3">
				<Button
					type="secondary"
					@click="$emit('close')"
				>
					{{ $t('crud.cancel') }}
				</Button>
				<Button
					ref="activateButton"
					type="primary"
					:disabled="!canActivate"
					@click="handleActivate"
				>
					{{ $t('crud.activate-job') }}
				</Button>
			</div>
		</div>
		</div>
		<!-- Backdrop -->
		<div class="fixed inset-0 z-40 bg-black bg-opacity-50"></div>
		</div>
	</transition>
</template>

<script>
import { mapActions, mapState } from 'vuex/dist/vuex.common.js'
import Toggle from '~/components/ui/form/Toggle'
import Icon from '~/components/ui/common/Icon'
import Button from '~/components/ui/common/Button'
import { hasOrganizationLinkedInCompany } from '~/utils/linkedinCompany'

export default {
	name: 'ActivateJobModal',
	components: {
		Toggle,
		Icon,
		Button
	},
	props: {
		jobOffer: {
			type: Object,
			required: true
		},
		organizationLinkedInUrl: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			show: false
		}
	},
	computed: {
		...mapState('activateJobOffer', ['selectedJobOffer']),
		enableMultiposting: {
			get() {
				return !!this.selectedJobOffer?.multiposting
			},
			set(value) {
				this.toggleMultiposting(value)
			}
		},
		hasConfiguredLinkedIn() {
			return hasOrganizationLinkedInCompany(
				this.$org,
				this.organizationLinkedInUrl
			)
		},
		canActivate() {
			return true
		}
	},
	methods: {
		...mapActions('activateJobOffer', [
			'setSelectedJobOffer',
			'toggleMultiposting'
		]),
		openLinkedInSettings() {
			if (!this.$can('edit organization')) return
			this.$emit('close')
			this.$router.push('/organization/profile')
		},
		handleActivate() {
			this.$emit('confirm')
		},
		globalEscapeHandler(ev) {
			if (ev.key === 'Escape') {
				ev.preventDefault()
				this.$emit('close')
			}
		}
	},
	mounted() {
		this.show = true
		this.setSelectedJobOffer(this.jobOffer)
		document.addEventListener('keydown', this.globalEscapeHandler)
		
		this.$nextTick(() => {
			// Focus modal container to trap focus within dialog
			if (this.$refs.modalContainer) {
				this.$refs.modalContainer.focus()
			}
			// Then focus the primary action button
			if (this.$refs.activateButton) {
				this.$refs.activateButton.$el.focus()
			}
		})
	},
	destroyed() {
		document.removeEventListener('keydown', this.globalEscapeHandler)
	}
}
</script>
